import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Home, Lock, Mail, ArrowLeft } from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HomeownerLogin = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowner/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('homeownerToken', data.token);
        localStorage.setItem('homeownerUser', JSON.stringify(data.homeowner));
        toast.success(language === 'es' ? '¡Bienvenido!' : 'Welcome!');
        navigate('/homeowner');
      } else {
        toast.error(data.detail || (language === 'es' ? 'Credenciales inválidas' : 'Invalid credentials'));
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D97706' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Language Toggle */}
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      {/* Back to Home */}
      <Link to="/" className="absolute top-4 left-4 flex items-center text-gray-600 hover:text-amber-600">
        <ArrowLeft className="h-4 w-4 mr-1" />
        {language === 'es' ? 'Inicio' : 'Home'}
      </Link>

      <Card className="w-full max-w-md shadow-2xl border-0 relative z-10">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: `${COMPANY.primaryColor}15` }}>
              <Home className="h-10 w-10" style={{ color: COMPANY.primaryColor }} />
            </div>
          </div>
          <div className="flex justify-center mb-4">
            <CompanyLogo className="h-12 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">
            {language === 'es' ? 'Portal del Cliente' : 'Client Portal'}
          </CardTitle>
          <p className="text-gray-500 text-sm mt-1">
            {language === 'es' ? 'Acceda al estado de su proyecto' : 'Access your project status'}
          </p>
        </CardHeader>
        
        <CardContent className="pt-4">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={language === 'es' ? 'Su correo electrónico' : 'Your email address'}
                  className="pl-10"
                  required
                  data-testid="homeowner-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'es' ? 'Contraseña' : 'Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Su contraseña' : 'Your password'}
                  className="pl-10"
                  required
                  data-testid="homeowner-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: COMPANY.primaryColor }}
              disabled={loading}
              data-testid="homeowner-login-btn"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {language === 'es' ? 'Accediendo...' : 'Logging in...'}
                </span>
              ) : (
                <>
                  <Home className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Acceder al Portal' : 'Access Portal'}
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-sm text-gray-500 mb-2">
              {language === 'es' ? '¿No tiene cuenta?' : "Don't have an account?"}
            </p>
            <Link 
              to="/homeowner/register" 
              className="inline-block px-4 py-2 rounded-md text-sm font-medium text-white hover:opacity-90"
              style={{ backgroundColor: COMPANY.primaryColor }}
            >
              {language === 'es' ? 'Registrarse' : 'Sign Up'}
            </Link>
          </div>

          <div className="mt-4 pt-4 border-t text-center">
            <p className="text-sm text-gray-500">
              {language === 'es' ? '¿Es contratista?' : 'Are you a contractor?'}
            </p>
            <Link 
              to="/login" 
              className="text-sm font-medium hover:underline"
              style={{ color: COMPANY.primaryColor }}
            >
              {language === 'es' ? 'Acceso de Administrador' : 'Admin Login'}
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-4 text-center text-sm text-gray-400">
        <p>{COMPANY.name} • {COMPANY.phone}</p>
      </div>
    </div>
  );
};

export default HomeownerLogin;
